import { DEFAULT_CURRENCY_CODE } from '../constants/currency.const';

/**
 * Formats a monetary amount using Intl.NumberFormat, which correctly handles
 * symbol placement for the given ISO 4217 currency code (prefix for USD/MYR,
 * suffix for some European currencies, etc.).
 *
 * Examples: formatCurrency(5, 'MYR') → 'RM5.00', formatCurrency(5, 'USD') → '$5.00'
 */
export function formatCurrency(
  amount: number,
  code: string = DEFAULT_CURRENCY_CODE
): string {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: code,
      currencyDisplay: 'narrowSymbol'
    }).format(amount);
  } catch {
    // Fallback for unrecognised code
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: DEFAULT_CURRENCY_CODE,
      currencyDisplay: 'narrowSymbol'
    }).format(amount);
  }
}

/**
 * Returns just the currency symbol for the given ISO 4217 code.
 * Uses Intl.NumberFormat's formatToParts to extract the symbol reliably.
 *
 * Examples: getCurrencySymbol('MYR') → 'RM', getCurrencySymbol('USD') → '$'
 */
export function getCurrencySymbol(
  code: string = DEFAULT_CURRENCY_CODE
): string {
  try {
    const parts = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: code,
      currencyDisplay: 'narrowSymbol'
    }).formatToParts(0);
    return parts.find((p) => p.type === 'currency')?.value ?? code;
  } catch {
    return DEFAULT_CURRENCY_CODE;
  }
}
